import React from 'react';
import { origin, SNOW_NXT_WORKFLOW_ENDPOINT, SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import queryString from 'query-string';
import EntityStatusComponent from '../../components/findEntity/EntityStatus';
import NoWorkflowExecutions from '../../components/findEntity/NoWorkflowExecutions';
import WorkflowSplitView from '../../components/findEntity/WorkflowSplitView';
import { WORKFLOW_EXECUTIONS_INFO_HEADER, WorkflowExecutionsInfoContent } from './FindEntityConstants';
import { v4 as uuidv4 } from 'uuid';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Grid from '@amzn/awsui-components-console/grid';
import Header from '@amzn/awsui-components-console/header';
import Container from '@amzn/awsui-components-console/container';
import Flashbar from '@amzn/awsui-components-console/flashbar';
import Popover from '@amzn/awsui-components-console/popover';
import Icon from '@amzn/awsui-components-console/icon';
import { snowIssueTemplate } from 'src/constants/CommonConstants';
import { RESILIENCE_STACK_SETUP_WORKFLOW } from '../../components/ResilienceWorkflow/ResilienceConstants';
import { getEntities, getWorkflowDefinitions } from 'src/utils/CommonUtils';
import { isOfSameWorkflow } from 'src/utils/AttributeUtils';
import { logger } from 'src/utils/logger';
import './FindEntity.scss';
import { getAwsCredentials } from 'src/authentication/cognito';
import { LoadingOverlay } from 'src/components/commonComponents/loadingOverlay/loadingOverlay';
import SegmentedControl from '@amzn/awsui-components-console/segmented-control';
import { EntityData, filterWorkflows, groupWorkflowsByStatus, isStartedByParent, orderWorkflowsByParent, shouldShowSplitView, sortWorkflowsByStatus, sortWorkflowsByRecency } from 'src/utils/workflowFilterUtils';
import Toggle from '@amzn/awsui-components-console/toggle';
import { ValidationExecutionStatus, ValidationReport, ValidationResult } from 'src/@types/validationTypes';
import AWS from 'aws-sdk';
import { useValidationPolling } from 'src/hooks/useValidationPolling';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { useAuthContext } from 'src/providers/AuthContext';
import { ApprovalType } from '@amzn/snow-data-service-typescript-client';
import { WorkflowDefinitionNode } from '@amzn/snow-nxt-workflow-service-typescript-client';

interface FlashbarItem {
    header: string;
    type: 'error' | 'warning' | 'success' | 'info';
    content: React.ReactNode;
    dismissible: boolean;
}

interface FindEntityProps {
    location: {
        search: string;
    };
    hideTools: () => void;
    updateValidationData?: (entityName: string, validationExecutionId: string | undefined, workflowType: string) => void;
    userName?: string;
    [key: string]: unknown;
}

interface FindEntityState {
    entitySearchValue: string;
    entityType: string;
    groupId: string;
    stack: string;
    isStack: boolean;
    isNode: boolean;
    showResults: boolean;
    entityData: EntityData[] | null;
    workflowsWithRuns: EntityData[];
    workflowsWithoutRuns: any[];
    loading: boolean;
    workflowType?: string;
    listSortMode: 'status' | 'recent';
    showSubWorkflows: boolean;
    flashBarItems?: FlashbarItem[];
    validationStates: {
        [workflowType: string]: {
            validationExecutionId?: string;
            validationStatus?: ValidationExecutionStatus | string;
            validationMessage?: string;
            isValidationPolling?: boolean;
            workflowExecutionStarted?: boolean;
            workflowExecutionError?: string;
            validationReport?: ValidationReport;
            validationResults?: ValidationResult[];
        };
    };
}

interface QueryParams {
    groupId?: string;
    workflowType?: string;
    fetchAllAttributes: boolean;
    requestId: string;
    entityName?: string;
}

interface AwsCredentials {
    accessKey: string;
    secretKey: string;
    sessionToken: string;
}

declare global {
    interface Window {
        AWS: unknown;
    }
}

const collectSubWorkflowIds = (
	subWorkflows?: WorkflowDefinitionNode[]
): string[] => {
	const workflowIds = new Set<string>();

	const addIdsFromLevel = (nodes?: WorkflowDefinitionNode[]) =>
		(nodes ?? []).forEach((node) => {
			if (node?.workflowDefinition?.workflowId) {
				workflowIds.add(node.workflowDefinition.workflowId);
			}
			addIdsFromLevel(node?.subWorkflows);
		});

	addIdsFromLevel(subWorkflows);

	return [...workflowIds];
};

class FindEntity extends React.Component<FindEntityProps, FindEntityState> {
	constructor(props: FindEntityProps) {
		super(props);
		props.hideTools();
		const queryEntity = queryString.parse(this.props.location.search).name as string || '';
		const entityType = queryString.parse(this.props.location.search).entityType as string || '';

		this.state = {
			entitySearchValue: queryEntity,
			entityType,
			groupId: queryString.parse(this.props.location.search).pipeline as string || '',
			stack: queryString.parse(this.props.location.search).stack as string || '',
			isStack: false,
			isNode: false,
			showResults: false,
			entityData: null,
			workflowsWithRuns: [],
			workflowsWithoutRuns: [],
			loading: false,
			listSortMode: 'status',
			showSubWorkflows: false,
			validationStates: {},
		};
		this.onClickTrigger = this.onClickTrigger.bind(this);
		this.handleTabChange = this.handleTabChange.bind(this);
		this.getQueryParams = this.getQueryParams.bind(this);
		this.handleValidationPassed = this.handleValidationPassed.bind(this);
	}

	setValidationState(workflowType: string, validationExecutionId: string | undefined, status: ValidationExecutionStatus | string | undefined, message: string | undefined, isPolling: boolean, validationReport?: any, validationResults?: any) {
		this.setState({
			validationStates: {
				...this.state.validationStates,
				[workflowType]: {
					validationExecutionId,
					validationStatus: status,
					validationMessage: message,
					isValidationPolling: isPolling,
					validationReport,
					validationResults,
				},
			},
		});
	}

	getValidationStateForActiveWorkflow() {
		const activeWorkflowType = this.state.workflowType ?? (this.state.entityData && this.state.entityData.length > 0 ? this.getActiveTab() : '');

		return this.state.validationStates[activeWorkflowType] || {};
	}

	async handleValidationPassed(entityName: string, workflowType: string) {
		const activeEntity = this.state.entityData?.find(
			entity => entity.WorkflowType === workflowType
		);

		if (activeEntity?.CycleId) {
			logger.info(`Workflow ${workflowType} already has CycleId ${activeEntity.CycleId}, skipping workflow execution start`);
			const newValidationStates = { ...this.state.validationStates };

			delete newValidationStates[workflowType];
			this.setState({
				validationStates: newValidationStates,
			});

			return;
		}

		if (activeEntity?.Status !== 'DRAFT') {
			logger.info(`Entity ${entityName} status is ${activeEntity?.Status}, not DRAFT. Skipping entity update and workflow execution start`);
			const newValidationStates = { ...this.state.validationStates };

			delete newValidationStates[workflowType];
			this.setState({
				validationStates: newValidationStates,
			});

			if (activeEntity?.Status === 'PENDING_APPROVAL') {
				this.onClickTrigger();
			}

			return;
		}

		const validationState = this.state.validationStates[workflowType];

		if (validationState?.workflowExecutionStarted) {
			logger.info(`Workflow execution already started for ${workflowType}, skipping duplicate call`);

			return;
		}

		this.setState({
			validationStates: {
				...this.state.validationStates,
				[workflowType]: {
					...this.state.validationStates[workflowType],
					workflowExecutionStarted: true,
				},
			},
		});

		try {
			await getAwsCredentials();
			const credentials: AwsCredentials = {
				accessKey: AWS.config.credentials?.accessKeyId || '',
				secretKey: AWS.config.credentials?.secretAccessKey || '',
				sessionToken: AWS.config.credentials?.sessionToken || '',
			};

			const workflowDefinitionResponse = await callSNOWDataService({
				path: `/workflows/${workflowType}`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				body: null,
				credentials: credentials as never,
			});

			const workflowDefinition = workflowDefinitionResponse?.workflowDefinition;

			let isApprovalRequired = (activeEntity?.EntityAttributes as any)?.approvalRequiredToStartExecution === 'true';

			if (!isApprovalRequired && workflowDefinition) {
				isApprovalRequired = workflowDefinition?.approvalRequiredToStartExecution === true;
			}

			const childWorkflowIds = collectSubWorkflowIds(
				workflowDefinitionResponse?.subWorkflows
			);

			await Promise.all(
				childWorkflowIds.map((childWorkflowId) => {
					logger.info(`Updating sub-workflow entity ${entityName} (${childWorkflowId}) status to READY`);

					return callSNOWDataService({
						path: `/entity/${childWorkflowId}/${entityName}`,
						method: 'PUT',
						url: SDS_APIGATEWAY_ENDPOINT,
						body: {
							updatedBy: this.props.userName || 'unknown',
							snowEntity: {
								entityName,
								workflowType: childWorkflowId,
								status: 'READY',
							},
							requestId: uuidv4(),
						},
						credentials: credentials as never,
					});
				})
			);

			logger.info(`Updating entity ${entityName} status to READY before ${isApprovalRequired ? 'sending for approval' : 'starting workflow'}`);
			await callSNOWDataService({
				path: `/entity/${workflowType}/${entityName}`,
				method: 'PUT',
				url: SDS_APIGATEWAY_ENDPOINT,
				body: {
					updatedBy: this.props.userName || 'unknown',
					snowEntity: {
						entityName,
						workflowType,
						status: 'READY',
					},
					requestId: uuidv4(),
				},
				credentials: credentials as never,
			});

			if (isApprovalRequired) {
				logger.info(`Sending approval request for ${entityName} with workflow ${workflowType}`);

				const approvalResponse = await callSNOWDataService({
					path: '/approvals',
					method: 'POST',
					url: SDS_APIGATEWAY_ENDPOINT,
					body: {
						approvalType: ApprovalType.WORKFLOW_EXECUTION,
						approvalConfiguration: {
							currConfig: '{}',
							newConfig: JSON.stringify(activeEntity),
							requestor: this.props.userName || credentials.accessKey,
						},
						requestId: uuidv4(),
						approvers: workflowDefinition?.workflowApprovalMetadata?.workflowExecutionApprovers?.map((approverMetadata: any) => ({
							type: approverMetadata.type,
							alias: approverMetadata.alias,
							level: approverMetadata.level,
						})) || [],
					},
					credentials: credentials as never,
				});

				const approvalId = approvalResponse?.approvalId;
				logger.info(`Successfully sent approval request for ${entityName}, approvalId: ${approvalId}`);
			} else {
				logger.info(`Starting workflow execution for ${entityName} with workflow ${workflowType}`);
				await callSNOWDataService({
					path: `/workflows/${workflowType}/executions`,
					method: 'POST',
					body: {
						entities: [entityName],
						startedBy: credentials.accessKey,
						requestId: uuidv4(),
					},
					url: SNOW_NXT_WORKFLOW_ENDPOINT,
					credentials: credentials as never,
				});

				logger.info(`Successfully started workflow execution for ${entityName}`);
			}

			// Keep the validation result so the Validation Record tab still shows it after the
			// refresh below; the polling hook won't refetch for the same validationExecutionId.
			this.setState({
				validationStates: {
					...this.state.validationStates,
					[workflowType]: {
						...this.state.validationStates[workflowType],
						isValidationPolling: false,
						workflowExecutionStarted: true,
					},
				},
			});

			this.onClickTrigger();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to start workflow execution';

			logger.error(`Error starting workflow execution after validation passed: ${error}`);
			
			this.setState({
				validationStates: {
					...this.state.validationStates,
					[workflowType]: {
						...this.state.validationStates[workflowType],
						workflowExecutionStarted: false,
						workflowExecutionError: errorMessage,
					},
				},
			});
		}
	}

	checkAndStartValidationPolling() {
		if (!this.state.entityData || this.state.entityData.length === 0) {
			return;
		}

		const activeWorkflowType = this.state.workflowType ?? this.getActiveTab();
		
		const activeEntity = this.state.entityData?.find(
			entity => entity.WorkflowType === activeWorkflowType
		);

		const validationExecutionId = (activeEntity?.ValidationExecutionId || activeEntity?.EntityData?.validationExecutionId) as string | undefined;
		const bulkExecutionId = (activeEntity?.BulkExecutionId || activeEntity?.EntityData?.bulkExecutionId) as string | undefined;
		const currentValidationState = this.state.validationStates[activeWorkflowType];

		if (validationExecutionId && !bulkExecutionId) {
			if (!currentValidationState || currentValidationState.validationExecutionId !== validationExecutionId) {
				this.setState({
					validationStates: {
						...this.state.validationStates,
						[activeWorkflowType]: {
							validationExecutionId,
							validationStatus: undefined,
							validationMessage: undefined,
							isValidationPolling: false,
						},
					},
				}, () => {
					if (this.props.updateValidationData) {
						this.props.updateValidationData(
							this.state.entitySearchValue,
							validationExecutionId,
							activeWorkflowType
						);
					}
				});
			} else {
				if (this.props.updateValidationData) {
					this.props.updateValidationData(
						this.state.entitySearchValue,
						validationExecutionId,
						activeWorkflowType
					);
				}
			}
		}
	}

	componentDidMount(): void {
		if (this.state.entitySearchValue !== '') {
			this.onClickTrigger();
		}
	}

	componentDidUpdate(prevProps: FindEntityProps): void {
		if (this.props.location.search !== prevProps.location.search) {
			const queryEntity = queryString.parse(this.props.location.search).name as string || '';
			const entityType = queryString.parse(this.props.location.search).entityType as string || '';

			this.setState({
				entitySearchValue: queryEntity,
				entityType,
				groupId: queryString.parse(this.props.location.search).pipeline as string || '',
				stack: queryString.parse(this.props.location.search).stack as string || '',
				showResults: false,
				loading: false,
			}, () => {
				if (this.state.entitySearchValue !== '' || this.state.groupId !== '' || this.state.stack !== '') {
					this.onClickTrigger();
				}
			});
		}
	}

	render(): JSX.Element {
		const entityData = this.state.entityData;
		const workflowsWithRuns = this.state.workflowsWithRuns;
		const workflowsWithoutRuns = this.state.workflowsWithoutRuns;
		const showSplitView = shouldShowSplitView(this.state.entityType);
		const activeValidationState = this.getValidationStateForActiveWorkflow();
		const hasSubWorkflows = Boolean(entityData?.some((entity) => isStartedByParent(entity.EntityAttributes)));
		const filterSubWorkflows = (entities: EntityData[]): EntityData[] => (
			this.state.showSubWorkflows ? entities : entities.filter((entity) => !isStartedByParent(entity.EntityAttributes))
		);
		const listedEntities = filterSubWorkflows(entityData ?? []);

		return (
			<div>
				<form className={'mt-2 mb-2 '} onSubmit={(e) => e.preventDefault()}>
					<LoadingOverlay hideOverlay={!this.state.loading} />
					<div>
						{
							this.state.showResults ?
								<Box variant="div">
									<Grid gridDefinition={[{ colspan: showSplitView ? 3 : 2 }, { colspan: showSplitView ? 9 : 10 }]}>
										<Container
											id="configList"
											header={
												<Header
													variant={showSplitView ? 'h2' : 'h3'}
													description={`Workflow Executions For the Entity ${this.state.entitySearchValue}`}
												>
													{showSplitView ? 'Workflow Executions' : (
														<span>
															<strong>Workflow Executions{entityData ? ` (${listedEntities.length})` : ''}</strong>{' '}
															<Popover
																header={WORKFLOW_EXECUTIONS_INFO_HEADER}
																triggerType="custom"
																size="medium"
																content={
																	<WorkflowExecutionsInfoContent
																		entityName={this.state.entitySearchValue}
																		hasSubWorkflows={hasSubWorkflows}
																	/>
																}
															>
																<Icon name="status-info" />
															</Popover>
														</span>
													)}
												</Header>
											}
										>
											{!showSplitView && (
												<Box padding={{ bottom: 'xs' }}>
													<div style={{ maxWidth: '100%', overflowX: 'auto' }}>
														<SegmentedControl
															selectedId={this.state.listSortMode}
															onChange={({ detail }) =>
																this.setState({ listSortMode: detail.selectedId as 'status' | 'recent' })
															}
															label="Sort workflow executions"
															options={[
																{ id: 'status', text: 'Status' },
																{ id: 'recent', text: 'Recent' },
															]}
														/>
													</div>
												</Box>
											)}
											{hasSubWorkflows && (
												<Box padding={{ bottom: 'xs' }}>
													<Toggle
														data-testid="showSubWorkflowsToggle"
														checked={this.state.showSubWorkflows}
														onChange={({ detail }) => this.setState({ showSubWorkflows: detail.checked })}
													>
														<strong><i>Show Sub-Workflows</i></strong>
													</Toggle>
												</Box>
											)}
											<ColumnLayout borders="horizontal" className="workflowList">
												{showSplitView ? (
													<WorkflowSplitView
														workflowsWithRuns={filterSubWorkflows(workflowsWithRuns)}
														workflowsWithoutRuns={workflowsWithoutRuns}
														activeWorkflowType={this.state.workflowType ?? this.getActiveTab()}
														onWorkflowChange={this.handleTabChange}
													/>
												) : this.state.listSortMode === 'recent' ? (
													<Box padding="s">
														{orderWorkflowsByParent(sortWorkflowsByRecency(listedEntities)).map(
															({ entity: { WorkflowType, workflowDefinitionName }, depth }, index) => (
																<div
																	key={WorkflowType}
																	className={depth > 0 ? 'workflowListItem workflowListItem--child' : 'workflowListItem'}
																	style={depth > 0 ? { marginLeft: `${depth * 16}px` } : undefined}
																>
																	<div className="workflowListItem__content">
																		<Button
																			data-testid={`configListItem-recent-${index + 1}`}
																			variant="link"
																			onClick={(e) => {
																				e.preventDefault();
																				this.handleTabChange(WorkflowType);
																			}}
																			id={WorkflowType === (this.state.workflowType ?? this.getActiveTab()) ? 'activeWorkflow' : undefined}
																		>
																			{workflowDefinitionName}
																		</Button>
																	</div>
																</div>
															)
														)}
													</Box>
												) : (
													groupWorkflowsByStatus(listedEntities).map((section) => (
														<Box key={section.key}>
															<Box variant="h5" fontWeight="bold" color="text-body-secondary" padding={{ top: 'xs' }}>
																{section.title} ({section.workflows.length})
															</Box>
															<Box padding="s">
																{section.workflows.map(
																	({ WorkflowType, workflowDefinitionName }, index) => (
																		<Box key={WorkflowType} margin={{ bottom: 'xxs' }}>
																			<Button
																				data-testid={`configListItem-${section.key}-${index + 1}`}
																				variant="link"
																				onClick={(e) => {
																					e.preventDefault();
																					this.handleTabChange(WorkflowType);
																				}}
																				id={WorkflowType === (this.state.workflowType ?? this.getActiveTab()) ? 'activeWorkflow' : undefined}
																			>
																				{workflowDefinitionName}
																			</Button>
																		</Box>
																	)
																)}
															</Box>
														</Box>
													))
												)}
											</ColumnLayout>
										</Container>
										<div>
											{!(this.state.workflowType ?? this.getActiveTab()) ? (
												<Box textAlign="center" color="text-body-secondary" padding="l">
													Please select a workflow type from the list to view its details.
												</Box>
											) : showSplitView ? (
												<>
													{entityData?.map(
														(entity) => (
															<React.Fragment key={entity.WorkflowType}>
																{entity.WorkflowType === (this.state.workflowType ?? this.getActiveTab()) &&
																	<EntityStatusComponent
																		entityName={this.state.entitySearchValue}
																		entityData={entity}
																		isStack={this.state.isStack}
																		workflowDefinitionName={entity.workflowDefinitionName}
																		validationExecutionId={activeValidationState.validationExecutionId}
																		validationStatus={activeValidationState.validationStatus}
																		validationMessage={activeValidationState.validationMessage}
																		isValidationPolling={activeValidationState.isValidationPolling}
																		workflowExecutionError={activeValidationState.workflowExecutionError}
																		validationReport={activeValidationState.validationReport}
																		validationResults={activeValidationState.validationResults}
																	/>
																}
															</React.Fragment>
														)
													)}
													{workflowsWithoutRuns.map((workflow) => (
														<React.Fragment key={workflow.workflowId}>
															{workflow.workflowId === (this.state.workflowType ?? this.getActiveTab()) &&
																<NoWorkflowExecutions 
																	entityName={this.state.entitySearchValue}
																	workflowId={workflow.workflowId}
																	workflowDefinitionName={workflow.workflowDefinitionName}
																/>
															}
														</React.Fragment>
													))}
												</>
											) : (
												entityData?.map(
													(entity) => (
														<React.Fragment key={entity.WorkflowType}>
															{entity.WorkflowType === (this.state.workflowType ?? this.getActiveTab()) &&
																<EntityStatusComponent
																	entityName={this.state.entitySearchValue}
																	entityData={entity}
																	isStack={this.state.isStack}
																	workflowDefinitionName={entity.workflowDefinitionName}
																	validationExecutionId={activeValidationState.validationExecutionId}
																	validationStatus={activeValidationState.validationStatus}
																	validationMessage={activeValidationState.validationMessage}
																	isValidationPolling={activeValidationState.isValidationPolling}
																	workflowExecutionError={activeValidationState.workflowExecutionError}
																	validationReport={activeValidationState.validationReport}
																	validationResults={activeValidationState.validationResults}
																/>
															}
														</React.Fragment>
													)
												)
											)}
										</div>
									</Grid>
								</Box>
								:
								!this.state.loading ? <div>
									<Flashbar items={this.state.flashBarItems || []} />
									<div className="container">
										<div className="row">
											<div className="col-md-12">
												<div className="error-template">
													<h1>
                                                        Oops!</h1>
													<h2>
                                                        Entity Not Found</h2>
													<div>
                                                        Please check the Entity Name! Or if the entity you are looking for is a draft entity in SNOWPortal please find them
														<a href={`https://snow.amazon.com/nodes/${this.state.entitySearchValue}`}> here</a>
													</div>
													<div className="error-actions">
														<Button iconName="undo" variant="primary" href={origin + '/#/entitySearch'}>
                                                            Dashboard
														</Button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div> : <div> </div>
						}
					</div>
				</form>
			</div>
		);
	}

	getActiveTab = (): string => {
		const parsedQuery = queryString.parse(this.props.location.search);

		if (parsedQuery.workflowType && !(parsedQuery.stack || parsedQuery.pipeline)) {
			return parsedQuery.workflowType as string;
		}

		if (!this.state.entityData || this.state.entityData.length === 0) {
			return '';
		}

		if (isOfSameWorkflow(this.state.entityData)) {
			return this.state.entityData![0].EntityName;
		}

		// Multiple distinct workflows found for a plain entityName search.
		// Don't default to the first one, require explicit selection.
		return '';
	};

	handleTabChange = (workflowType: string): void => {
		const [baseHref, existingQuery = ''] = window.location.href.split('?');
		const params = queryString.parse(existingQuery);
		params.workflowType = workflowType;

		const newQuery = queryString.stringify(params);
		const newHref = newQuery ? `${baseHref}?${newQuery}` : baseHref;

		window.history.replaceState(undefined, 'SNOW App', newHref);
		this.setState({ workflowType }, () => {
			this.checkAndStartValidationPolling();
		});
	};

	async onClickTrigger(): Promise<void> {
		this.setState({
			loading: true,
			showResults: false,
		});

		const body = this.getQueryParams();

		try {
			await getAwsCredentials();
			const credentials: AwsCredentials = {
				accessKey: AWS.config.credentials?.accessKeyId || '',
				secretKey: AWS.config.credentials?.secretAccessKey || '',
				sessionToken: AWS.config.credentials?.sessionToken || '',
			};

			const [getEntititesResponse, listWorkflowDefinitionResponse] = await Promise.all([
				getEntities(body, credentials as never), 
				getWorkflowDefinitions({ requestId: uuidv4(), entityType: this.state.entityType }, credentials as never),
			]);

			const entities = getEntititesResponse.entities;

			const dataMap = new Map(listWorkflowDefinitionResponse.workflowDefinitions.map(
				(workflow: { workflowId: string; workflowDefinitionName: string }) => [workflow.workflowId, workflow.workflowDefinitionName]
			));

			const entityData: EntityData[] = sortWorkflowsByStatus(
				entities.map((item: EntityData) => ({
					...item,
					workflowDefinitionName: dataMap.get(item.WorkflowType) || item.WorkflowType,
				}))
			);

			// Use filtering logic for specific entity types
			if (shouldShowSplitView(this.state.entityType)) {
				const entityType = entities[0]?.EntityType || this.state.entityType;
				const { workflowsWithRuns, workflowsWithoutRuns } = filterWorkflows(
					entityData, 
					listWorkflowDefinitionResponse.workflowDefinitions, 
					entityType
				);

				this.setState({
					entityData,
					workflowsWithRuns,
					workflowsWithoutRuns,
					loading: false,
					showResults: entityData && entityData.length > 0,
				}, () => {
					this.checkAndStartValidationPolling();
				});
			} else {
				this.setState({
					entityData,
					workflowsWithRuns: [],
					workflowsWithoutRuns: [],
					loading: false,
					showResults: entityData && entityData.length > 0,
				}, () => {
					this.checkAndStartValidationPolling();
				});
			}
		} catch (err) {
			logger.error(`Error occurred while fetching entity data from SDS ${err}`);
			this.setState({
				flashBarItems: [{
					header: `Failed to fetch entity data of ${this.state.entitySearchValue}`,
					type: 'error',
					content: <p>Please reach out to SNOW Team. Issue <a href={snowIssueTemplate} target="_blank" rel="noopener noreferrer">link</a></p>,
					dismissible: true,
				}],
				loading: false,
				showResults: false,
			});
		}
	}

	getQueryParams = (): QueryParams => {
		if (this.state.groupId) {
			this.setState({ isStack: true });

			return {
				groupId: this.state.groupId,
				workflowType: RESILIENCE_STACK_SETUP_WORKFLOW,
				fetchAllAttributes: true,
				requestId: uuidv4(),
			};
		} else if (this.state.stack) {
			this.setState({ isStack: true });

			return {
				entityName: this.state.stack,
				fetchAllAttributes: true,
				requestId: uuidv4(),
			};
		} else {
			this.setState({ isStack: false });

			return {
				entityName: this.state.entitySearchValue,
				fetchAllAttributes: true,
				requestId: uuidv4(),
			};
		}
	};
}

const FindEntityWithValidation: React.FC<FindEntityProps> = (props) => {
	const findEntityRef = React.useRef<FindEntity>(null);
	const { userName } = useAuthContext();
	const [validationData, setValidationData] = React.useState<{
		entityName: string;
		validationExecutionId: string | undefined;
		workflowType: string;
	}>({
		entityName: '',
		validationExecutionId: undefined,
		workflowType: '',
	});

	const validationState = useValidationPolling(
		validationData.entityName,
		validationData.validationExecutionId,
		validationData.workflowType,
		(entityName, workflowType) => {
			if (findEntityRef.current) {
				findEntityRef.current.handleValidationPassed(entityName, workflowType);
			}
		}
	);

	React.useEffect(() => {
		if (findEntityRef.current && validationState && validationData.workflowType) {
			findEntityRef.current.setValidationState(
				validationData.workflowType,
				validationState.validationExecutionId,
				validationState.status,
				validationState.message,
				validationState.isPolling,
				validationState.validationReport,
				validationState.validationResults
			);
		}
	}, [validationState, validationData.workflowType]);

	const updateValidationData = React.useCallback((entityName: string, validationExecutionId: string | undefined, workflowType: string) => {
		setValidationData({ entityName, validationExecutionId, workflowType });
	}, []);

	return (
		<FindEntity
			{...props}
			ref={findEntityRef}
			updateValidationData={updateValidationData}
			userName={userName}
		/>
	);
};

export { FindEntity };
export default FindEntityWithValidation;